/**
 * SunGrow codes by NAME — the title a fault code or work state reads as, and the kind of problem
 * it is. Kept beside the table (`sungrow-codes.ts`) and free of any other import so the shell's
 * toasts can use it without pulling the device-facts module into the not-lazy bundle
 * (`components/lumen/alarm-readable.ts`'s budget note). The full alarm wording — origin line,
 * tone, category — is `modules/alarms/alarm-words.ts`, built on this.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE NAME PER CODE, AND IT IS THE ONE ON THE FAULT CODES SCREEN (2026-10-08)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Until today this file carried its own plain-word title for every family — 548 read "String 1
 * producing below normal" — while the catalogue's label, which Alarms → Fault codes edits, read
 * "PV abnormal alarm — string 1 (0x0224)". The same fault therefore wore two names on one page:
 * the device's alarm strip quoted the catalogue, its Fault code card and the board quoted this
 * file, and a code an administrator RENAMED changed on one of them and not the other.
 *
 * So the catalogue's label is now the title wherever a caller has it (`AlarmRow.name`, a
 * channel's decoded `text`), with its "(0x0224)" dropped from the headline — the register code is
 * a technician's detail and lives on the code line. Where a caller has only the raw identity (a
 * toast reads `sungrow_fault_code=552`), the title is SunGrow's own default for that code, built
 * exactly as the backend seeds the catalogue ("PV abnormal alarm — string 5"), so an untouched
 * code reads the same in a toast as on the board. A renamed one shows its new name everywhere a
 * label travels; the toast alone keeps the default, having no label to read.
 */
import { sungrowFaultCode, sungrowWorkState, type SungrowFamily, type SungrowCodeInfo } from './sungrow-codes';

export type CodeKind =
  | 'string'
  | 'reverse'
  | 'grid-voltage'
  | 'grid-frequency'
  | 'grid'
  | 'insulation'
  | 'temperature'
  | 'arc'
  | 'meter'
  | 'comms'
  | 'system';

export type StateKind = 'alarm-run' | 'fault-run' | 'stop' | 'comms' | 'derating';

type Unit = SungrowCodeInfo['unit'];

/** Which kind of problem a family is — what the filters and the categories count by. */
export function faultKind(family: SungrowFamily): CodeKind {
  switch (family) {
    case 'pv-abnormal':
      return 'string';
    case 'pv-reverse-alarm':
    case 'pv-reverse-fault':
    case 'mppt-reverse':
    case 'reflux':
      return 'reverse';
    case 'grid-overvoltage':
    case 'grid-undervoltage':
    case 'grid-imbalance':
      return 'grid-voltage';
    case 'grid-overfrequency':
    case 'grid-underfrequency':
      return 'grid-frequency';
    case 'grid-outage':
    case 'grid-abnormal':
    case 'grid-confrontation':
      return 'grid';
    case 'leakage':
    case 'insulation':
    case 'grounding-cable':
    case 'pv-grounding':
      return 'insulation';
    case 'temp-high':
    case 'temp-low':
      return 'temperature';
    case 'arc':
      return 'arc';
    case 'meter-reverse':
      return 'meter';
    case 'meter-comms':
    case 'parallel-comms':
      return 'comms';
    case 'boost-alarm':
    case 'boost-fault':
    case 'hardware':
    case 'system-alarm':
    case 'system-fault':
      return 'system';
  }
}

/* "(0x0224)" or "(0224)" at the end of a label — the catalogue appends the register code. */
const TRAILING_CODE = /\s*\((?:0x)?[0-9a-f]{4}\)\s*$/i;
/* A raw identity (`sungrow_fault_code=552`) or a bare number is not a name. */
const NOT_A_NAME = /^(?:-?\d+(?:\.\d+)?|[a-z0-9_]+=-?\d+(?:\.\d+)?)$/;

/**
 * A catalogue label as a headline: "PV abnormal alarm — string 1 (0x0224)" → "PV abnormal alarm —
 * string 1". `null` for no label, a bare number, or a raw identity — the caller falls back to
 * SunGrow's default rather than printing a key as a name.
 */
export function plainLabel(label: string | null | undefined): string | null {
  if (!label) return null;
  const t = label.replace(TRAILING_CODE, '').trim();
  return t === '' || NOT_A_NAME.test(t) ? null : t;
}

/** SunGrow's own name for a code, as the backend seeds the catalogue — "PV abnormal alarm —
 *  string 5", "Grid overvoltage". `null` for a code outside Appendix 3. */
export function sungrowDefaultName(code: number): string | null {
  const info = sungrowFaultCode(code);
  if (!info) return null;
  return `${info.vendor}${unitWords(info.unit)}`;
}

function unitWords(unit: Unit): string {
  return unit ? ` — ${unit.noun} ${unit.n}` : '';
}

/**
 * A fault code in words: title, kind, the code line and its class; `null` when the code is not in
 * SunGrow's list (the caller says so rather than borrowing a meaning). `label` is the catalogue's
 * — pass it whenever you hold it, so a renamed code reads by its new name.
 */
export function sungrowCodeWords(
  code: number,
  label?: string | null,
): { title: string; kind: CodeKind; code: string; cls: 'fault' | 'alarm'; unit: Unit } | null {
  const info = sungrowFaultCode(code);
  if (!info) return null;
  return {
    title: plainLabel(label) ?? `${info.vendor}${unitWords(info.unit)}`,
    kind: faultKind(info.family),
    code: `SunGrow code ${code} (${info.hex})`,
    cls: info.cls,
    unit: info.unit,
  };
}

export function unknownCodeLine(code: number): string {
  return `SunGrow code ${code} (0x${(code & 0xffff).toString(16).toUpperCase().padStart(4, '0')}) · not in SunGrow's code list`;
}

const STATE_KIND: Record<NonNullable<ReturnType<typeof sungrowWorkState>>['kind'], StateKind> = {
  'alarm-run': 'alarm-run',
  'fault-run': 'fault-run',
  'emergency-stop': 'stop',
  'comms-fault': 'comms',
  derating: 'derating',
  stop: 'stop',
};

/** A work state in words; `null` for a state that is not an alarm condition in Appendix 1. Same
 *  naming rule as a fault code: the catalogue's label without its code, else SunGrow's name. */
export function sungrowStateWords(
  value: number,
  label?: string | null,
): { title: string; kind: StateKind; code: string; cls: 'fault' | 'alarm' } | null {
  const st = sungrowWorkState(value);
  if (!st) return null;
  return { title: plainLabel(label) ?? st.vendor, kind: STATE_KIND[st.kind], code: `SunGrow state ${st.hex}`, cls: st.cls };
}
